import { Link } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import type { Project } from '@/types'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'

const categoryLabel: Record<Project['category'], string> = {
  ml: 'Machine Learning',
  optimization: 'Optimization',
  research: 'Research',
  infra: 'Infrastructure',
}

/** A gradient placeholder keyed off the project id so each card differs. */
function ProjectThumb({ project }: { project: Project }) {
  if (project.image) {
    return (
      <img
        src={project.image}
        alt={project.title}
        className="h-40 w-full object-cover"
      />
    )
  }
  const gradients = [
    'from-sky-500/25 to-indigo-500/25',
    'from-emerald-500/25 to-teal-500/25',
    'from-fuchsia-500/25 to-purple-500/25',
    'from-amber-500/25 to-orange-500/25',
  ]
  const g = gradients[project.id.length % gradients.length]
  return (
    <div
      className={`flex h-40 w-full items-center justify-center bg-gradient-to-br ${g}`}
    >
      <span className="text-sm font-semibold uppercase tracking-widest text-foreground/50">
        {categoryLabel[project.category]}
      </span>
    </div>
  )
}

export function ProjectCard({ project }: { project: Project }) {
  return (
    <Link to={`/projects/${project.id}`} className="group block">
      <Card className="h-full overflow-hidden transition-colors hover:border-foreground/30">
        <ProjectThumb project={project} />
        <CardContent className="space-y-3 p-5">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold leading-snug group-hover:text-primary">
              {project.title}
            </h3>
            <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </div>
          <p className="line-clamp-3 text-sm text-muted-foreground">
            {project.description}
          </p>
          <div className="flex flex-wrap gap-1.5 pt-1">
            {project.tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="secondary" className="font-normal">
                {tag}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </Link>
  )
}
